import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CarouselContent,
  Column,
  Copy,
  Media as DesignSystemMedia,
  Optional,
  Row,
  useThemeContext,
  Icon,
} from '@honda-canada/design-system-react';

import { useTranslation } from 'react-i18next';
import { useProductCatalogDataTree, useProductCompareData } from './ProductCompareDataContextProvider';
import { Print, Screen } from '../../utils/components/ForPrint.styles';
import ProductSelector from './ProductSelector';
import ProductCard from './ProductCard';
import ProductSpecifications from './ProductSpecifications';
import CarouselPaginator from './CarouselPaginator';
import { useActiveProducts, useCarouselSlider } from './ProductCompareContextProvider';
import useSelectedModels from './useSelectedModels';
import useDetectStickyProductSelector from './useDetectStickyProductSelector';
import { PRODUCT_COMPARE_ID_KEY } from '../../utils/constants';
import themeStyles from './styles/ProductCompare.styles';
import { EqualHeight } from '../../utils/components/EqualHeight';
import useStickyHeightConfiguration from './useStickyHeightConfiguration';
import DealerInventoryModal from '../TrimPackages/Details/DealerInventoryModal';
import { useAppSelector } from '../../core/utils';
import { selectBodyTypesForGtm } from '../../core/reducers/vehicles';
import gtmDealerInventoryBuyNow from '../../core/gtm/dealer_inventory/gtmDealerInventoryBuyNow';
import useProductFinancial from '../../apiHooks/useProductFinancial';
import safelyStringifyJSON from '../../utils/safelyStringifyJSON';
import getModelPePricing from '../../utils/getModelPePricing';
import useMeasureCarouselHeight from './useMeasureCarouselHeight';
import usePriceFlagsForProvince from '../../utils/hooks/usePriceFlagsForProvince';

const Media = themeStyles.apply(DesignSystemMedia, 'Media');
const ProductCompareRow = themeStyles.apply(Row, 'ProductCompareRow');
const ProductCompareColumn = themeStyles.apply(Column, 'ProductCompareColumn');
const ProductSelectorColumn = themeStyles.apply(Column, 'ProductSelectorColumn');
const NoOptionsSelected = themeStyles.apply(Copy, 'NoOptionsSelected');
const CarouselContainer = themeStyles.apply(Box, 'CarouselContainer');
const DownloadCta = themeStyles.apply(Box, 'DownloadCta');
const DownloadCopy = themeStyles.apply(Copy, 'DownloadCopy');

const CompareLayout = ({ onRemove, onSelect, onUpdate, gtmTags, variant }) => {
  const refProductCompareRow = useRef(null);
  const carouselHeight = useMeasureCarouselHeight(refProductCompareRow);

  const { t } = useTranslation();
  const { header } = useThemeContext();
  const productCatalogDataTree = useProductCatalogDataTree(); // Todo: request modelCode be returned here;
  const [activeProducts, { removeProduct, onProductSelected, setProducts }] = useActiveProducts();
  const selectedModels = useSelectedModels(activeProducts, productCatalogDataTree, onUpdate, setProducts);
  const { stickyHeight } = useStickyHeightConfiguration(variant, header?.desktop?.height);
  const { isSticky } = useDetectStickyProductSelector(refProductCompareRow);
  const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);

  const [showDealerInventoryModal, setShowDealerInventoryModal] = useState(false);
  const [modelCodeForInventoryModal, setModelCodeForInventoryModal] = useState('');

  // FIXME: This is not the right way to fetch payment, as it trigger multiple API requests adding each models sequentially.
  const [modelDetailForInventoryModal, setModelDetailForInventoryModal] = useState(null);

  const { viewDetailsButton, selectProductsToCompare, isDownloadCtaValid } = useProductCompareData();
  const { financial, setModels } = useProductFinancial({});
  const { showSellingPrice } = usePriceFlagsForProvince();

  useEffect(() => {
    setModels(
      selectedModels
        .filter(model => !!model?.detModelKey)
        .map(({ detModelKey }) => ({
          modelKey: detModelKey,
          modelYear: 9999,
        })),
    );
  }, [safelyStringifyJSON(selectedModels)]);

  const productsSelectorList = useMemo(() => {
    const onRemoveOption = columnIndex => {
      const id = activeProducts[columnIndex];
      removeProduct(id);
      onRemove(id);
    };

    const onDropdownItemSelected = data => {
      const id = data?.[PRODUCT_COMPARE_ID_KEY];
      if (id) {
        onSelect(id);
        onProductSelected(id);
      }
    };

    const isFullProductCards = selectedModels?.every(model => !!model);
    return selectedModels?.map((currentOption, index) => {
      if (!currentOption) {
        return (
          <ProductSelectorColumn
            data-testid={`product-compare-column${index}`}
            width={[1, 1, 1 / 5]}
            key={`${index.toString()}`}
          >
            <ProductSelector
              productCatalogDataTree={productCatalogDataTree}
              selectorIndex={index}
              activeProducts={activeProducts}
              onProductSelected={onDropdownItemSelected}
              indexId={index}
            />
          </ProductSelectorColumn>
        );
      }

      function handleBuyNowClick() {
        const { detModelKey, detKey, modelCode, detTransmissionId, model: productName } = currentOption;

        const price = getModelPePricing(financial, {
          trimKey: detKey,
          modelKey: detModelKey,
          trimId: currentOption[PRODUCT_COMPARE_ID_KEY],
          transmissionId: detTransmissionId,
        });

        gtmDealerInventoryBuyNow({
          productId: modelCode,
          productName,
          discountAmount: price?.discount?.priceDiscountAmount,
          bodyStyle: gtmBodyStyle,
          price: showSellingPrice ? price?.sellingPrice : price?.msrp,
          category: gtmTags['data-gtm-category'],
        });

        const gtmTagging = {
          gtmTrimName: productName,
          gtmCategoryName: gtmTags['data-gtm-category'],
          gtmBodyStyle,
          price: showSellingPrice ? price?.sellingPrice : price?.msrp,
          discountAmount: price?.discount?.priceDiscountAmount,
        };

        setModelDetailForInventoryModal({ ...gtmTagging });
        setShowDealerInventoryModal(true);
        setModelCodeForInventoryModal(modelCode);
      }

      return (
        <ProductCompareColumn
          width={[1, 1, 1 / 5]}
          bg={['white', 'white', index % 2 ? 'white' : 'grey.5']}
          key={`${index.toString()}-${currentOption.detIdentifier}`}
          isFullProductCards={isFullProductCards}
        >
          <ProductCard
            product={currentOption}
            onRemoveOption={onRemoveOption}
            productIndex={index}
            gtmTags={gtmTags}
            handleBuyNowClick={handleBuyNowClick}
            viewDetailsButton={viewDetailsButton}
            isSticky={isSticky}
            isFullProductCards={isFullProductCards}
          />
        </ProductCompareColumn>
      );
    });
  }, [activeProducts, isSticky, financial]);

  // Carousel state and update functions
  const { prevSlide, nextSlide, setCurrentSlide, currentSlide, bindDrag, pages } = useCarouselSlider();

  const pdfLabel = t('Shared.CompareTrims.saveAsPdfButton');
  return (
    <>
      <Screen>
        <Media lessThan="desktop" stickyHeight={stickyHeight}>
          <ProductCompareRow style={{ border: 'none' }} ref={refProductCompareRow}>
            <CarouselContainer showSticky={isSticky} hideSticky={!isSticky}>
              <CarouselContent
                currentSlide={currentSlide}
                bindDrag={bindDrag}
                items={productsSelectorList}
                equalHeight
                slideAnimation
              />
              <CarouselPaginator
                pages={pages}
                currentSlide={currentSlide}
                prevSlide={prevSlide}
                nextSlide={nextSlide}
                setCurrentSlide={setCurrentSlide}
              />
            </CarouselContainer>
          </ProductCompareRow>
        </Media>
        <Media
          greaterThan="smallDesktop"
          style={{ paddingBottom: isSticky ? `${refProductCompareRow?.current?.offsetTop}px` : '0px' }}
          stickyHeight={stickyHeight}
        >
          <CarouselContainer ref={refProductCompareRow} showSticky={isSticky} hideSticky={!isSticky}>
            <EqualHeight>
              <Screen>
                <ProductCompareRow style={{ border: 'none' }}>
                  <ProductCompareColumn width={1 / 5}>
                    <Optional when={isDownloadCtaValid}>
                      <DownloadCta as="a" onClick={() => window.print()}>
                        <Icon name="save" mr="xs" />
                        <DownloadCopy>{pdfLabel}</DownloadCopy>
                      </DownloadCta>
                    </Optional>
                  </ProductCompareColumn>
                  {productsSelectorList}
                </ProductCompareRow>
              </Screen>
            </EqualHeight>
          </CarouselContainer>
        </Media>
        <Optional when={activeProducts.length > 0}>
          <ProductSpecifications selectedModels={selectedModels} isSticky={isSticky} carouselHeight={carouselHeight} />
        </Optional>
        <Optional when={activeProducts.length <= 0}>
          <NoOptionsSelected
            data-testid="compare-no-selection-text"
            mt="xl"
            mb="huge"
            display="flex"
            justifyContent="center"
          >
            {selectProductsToCompare}
          </NoOptionsSelected>
        </Optional>
      </Screen>

      <DealerInventoryModal
        showModal={showDealerInventoryModal}
        modelCode={modelCodeForInventoryModal}
        closeModal={() => setShowDealerInventoryModal(false)}
        gtmTagging={modelDetailForInventoryModal}
      />

      {/* Layout to Print PDF */}
      <Optional when={isDownloadCtaValid}>
        <Print>
          <Row style={{ border: 'none', display: 'flex', flexDirection: 'row' }}>
            <ProductCompareColumn width={1 / 5}>
              <DownloadCta as="a">
                <Icon name="save" mr="xs" />
                <DownloadCopy>{pdfLabel}</DownloadCopy>
              </DownloadCta>
            </ProductCompareColumn>
            <ProductCompareColumn width={1 / 5}>
              <Optional when={selectedModels[0]?.detIdentifier}>
                <ProductCard product={selectedModels[0]} gtmTags={gtmTags} viewDetailsButton={viewDetailsButton} />
              </Optional>
              <Optional when={!selectedModels[0]?.detIdentifier}>
                <ProductSelector productCatalogDataTree={productCatalogDataTree} activeProducts={activeProducts} />
              </Optional>
            </ProductCompareColumn>
            <ProductCompareColumn width={1 / 5}>
              <Optional when={selectedModels[1]?.detIdentifier}>
                <ProductCard product={selectedModels[1]} gtmTags={gtmTags} viewDetailsButton={viewDetailsButton} />
              </Optional>
              <Optional when={!selectedModels[1]?.detIdentifier}>
                <ProductSelector productCatalogDataTree={productCatalogDataTree} activeProducts={activeProducts} />
              </Optional>
            </ProductCompareColumn>
            <ProductCompareColumn width={1 / 5}>
              <Optional when={selectedModels[2]?.detIdentifier}>
                <ProductCard product={selectedModels[2]} gtmTags={gtmTags} viewDetailsButton={viewDetailsButton} />
              </Optional>
              <Optional when={!selectedModels[2]?.detIdentifier}>
                <ProductSelector productCatalogDataTree={productCatalogDataTree} activeProducts={activeProducts} />
              </Optional>
            </ProductCompareColumn>
            <ProductCompareColumn width={1 / 5}>
              <Optional when={selectedModels[3]?.detIdentifier}>
                <ProductCard product={selectedModels[3]} gtmTags={gtmTags} viewDetailsButton={viewDetailsButton} />
              </Optional>
              <Optional when={!selectedModels[3]?.detIdentifier}>
                <ProductSelector productCatalogDataTree={productCatalogDataTree} activeProducts={activeProducts} />
              </Optional>
            </ProductCompareColumn>
          </Row>
          <ProductSpecifications selectedModels={selectedModels} isSticky={isSticky} isPrinted />
        </Print>
      </Optional>
    </>
  );
};

CompareLayout.propTypes = {
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  gtmTags: PropTypes.shape({
    'data-gtm-component-type': PropTypes.string,
    'data-gtm-interaction-type': PropTypes.string,
    'data-gtm-title': PropTypes.string,
  }),
  variant: PropTypes.string,
};

export default CompareLayout;
